import React, { useContext, useEffect, useRef, useState } from 'react';
import AnswerCard from 'views/Questions/components/AnswerCard';
import RecommendationCard from 'views/Questions/components/RecommendationCard';
import NoAnswers from 'views/Questions/components/NoAnswers';
import UserQuestion from 'views/Questions/components/UserQuestion';
import useWindowWidth from 'utils/useWindowWidth';
import scrollToRef from 'utils/scrollToRef';
import { QuestionData } from 'views/Questions/Question.types';
import Banner from 'views/QuestionsWithCategories/components/Banners';
import { appAuthContext } from 'context/AuthContext';
import { configuratorContext } from 'context/ConfiguratorContext';
import VKAd from 'components/VKAd';
import { Api } from 'api/otvety';
import AIAnswerCard from './components/AIAnswerCard/AIAnswerCard';
import styles from './Question.module.css';

type QuestionProps = {
  data: QuestionData;
  userParallel: number | null;
  marusiaAnswer?: Api.GetMarusiaData200Response.MarusiaAnswer;
};

const Question = ({ data, userParallel, marusiaAnswer }: QuestionProps) => {
  const { user } = useContext(appAuthContext);
  const { banners } = useContext(configuratorContext);
  const { question, answers, recommendedCourseData, courseLinksData } = data;

  const [userWriteAnswer, setUserWriteAnswer] = useState(false);
  const userHasAnswer = answers.filter((i) => i.userIsAuthor).length > 0;

  const answerContainerRef = useRef<HTMLDivElement | null>(null);
  const answerFormRef = useRef<HTMLDivElement | null>(null);
  const answerSectionRef = useRef<HTMLDivElement | null>(null);
  const answerFromUserRef = useRef<HTMLDivElement | null>(null);
  const isMounted = useRef(false);

  useEffect(() => {
    if (answerSectionRef) {
      scrollToRef(answerSectionRef, '#questions-page__answers');
    }
  }, [answerSectionRef]);

  const deviceWidth = useWindowWidth();
  const showDesktopBanner = deviceWidth > 1023 && deviceWidth > 0;

  const showRecommendationCard =
    !(
      user?.azTests?.otvety_global_student === 'control' ||
      user?.data?.attributes?.type !== 'student'
    ) || !user?.data?.attributes?.foreign_id;

  useEffect(() => {
    if (answerFormRef?.current && isMounted.current) {
      answerFormRef?.current?.scrollIntoView({ behavior: 'smooth' });
      setUserWriteAnswer(false);
    } else {
      isMounted.current = true;
    }
  }, [userWriteAnswer]);

  return (
    <>
      <div className={styles.question}>
        <UserQuestion
          userWriteAnswer={userWriteAnswer}
          ref={answerFormRef}
          answerFromUser={answerFromUserRef}
          data={question}
          userHasAnswer={userHasAnswer}
          courseLinksData={courseLinksData}
        />
        <div className={styles.question__container} ref={answerContainerRef}>
          <h2 className={styles.question__title}>
            Ответы
            {question.publishedAnswersCount > 0 && (
              <span className={styles.question__title_counter}>
                {question.publishedAnswersCount}
              </span>
            )}
          </h2>
          <div className={styles.question__content_wrapper}>
            <div className={styles.question__content} ref={answerSectionRef}>
              {answers.length < 1 && !marusiaAnswer && (
                <NoAnswers
                  type={question.questionState}
                  isAuthor={question.userIsAuthor}
                  setUserWriteAnswer={setUserWriteAnswer}
                />
              )}
              {marusiaAnswer && (
                <AIAnswerCard
                  marusiaAnswer={marusiaAnswer}
                  parentRef={answerContainerRef}
                  hasAnswers={answers.length > 0}
                />
              )}
              {answers &&
                answers.length > 0 &&
                answers.map((item, index) => (
                  <React.Fragment key={item.id}>
                    <AnswerCard
                      ref={item.userIsAuthor ? answerFromUserRef : null}
                      answerData={item}
                    />
                    {index === 0 && showRecommendationCard && (
                      <RecommendationCard
                        data={recommendedCourseData}
                        userParallel={userParallel}
                      />
                    )}
                  </React.Fragment>
                ))}
              {!showDesktopBanner && banners?.otvety_side_banners && (
                <Banner
                  width={256}
                  height={160}
                  linkFromBanner={banners?.otvety_side_banners?.link}
                  imageUrl={banners?.otvety_side_banners?.images?.mobile}
                  backgroundColor={banners?.otvety_side_banners?.background}
                  bannerType="mobile"
                  eventInfo="banner_side_mobile"
                  className={styles.mobile_banner}
                  bannerName={banners?.otvety_side_banners?.name}
                />
              )}
              {banners?.otvety_side_banners === null && !showDesktopBanner && (
                <VKAd
                  slotId="1531134"
                  maxWidth="100%"
                  maxHeight="250px"
                  className={styles.ad_wrapper}
                />
              )}
            </div>
            <div className={styles.question__sidebar}>
              {showDesktopBanner && banners?.otvety_side_banners && (
                <Banner
                  width={300}
                  height={418}
                  className={styles.banner}
                  linkFromBanner={banners?.otvety_side_banners?.link}
                  imageUrl={banners?.otvety_side_banners?.images?.desktop}
                  bannerType="desktop"
                  eventInfo="banner_side"
                  bannerName={banners?.otvety_side_banners?.name}
                />
              )}
              {banners?.otvety_side_banners === null &&
                showDesktopBanner &&
                !user?.data?.attributes && (
                  <VKAd slotId="1531120" maxWidth="300px" maxHeight="600px" />
                )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Question;
